import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SlideFade,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import React, { useState } from 'react';
import api from '../services/api';
import { Toast } from './Toast';
import { CiWarning } from 'react-icons/ci';
import { DEFAULT_EXPORT_FORMAT, EXPORT_TYPES, ExportFormatType } from '../domain/ExportFormat.const';

interface ExportButtonProps<T extends object> {
  params: T;
  fileName: string;
  path: string;
  availableFormats?: ExportFormatType[];
  label?: string;
}

const DEFAULT_LABEL = 'Descargar';

const ExportButton = <T extends object>(props: ExportButtonProps<T>) => {
  const [loadingFile, setLoadingFile] = useState<boolean>(false);
  const [waitForLoad, setWaitForLoad] = useState<boolean>(false);
  const toast = useToast();

  const availableFormats = props.availableFormats || DEFAULT_EXPORT_FORMAT;
  const isDropdown = availableFormats.length > 1;
  const label = props.label ?? DEFAULT_LABEL;

  const handleDownload = (format: ExportFormatType) => {
    setLoadingFile(true);
    setWaitForLoad(false);
    const time = setTimeout(() => {
      setWaitForLoad(true);
    }, 4000);

    api.get(`${props.path}`, {
      responseType: 'blob',
      timeout: 0,
      params: {
        ... props.params,
        format
      },
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${props.fileName}-${new Date().toDateString()}.${format}`);
      link.click();
    }).catch((error) => {
      const message = 'No pudimos generar el reporte, por favor espere unos minutos y vuelva a intentarlo.';
      const ref = toast({
        position: 'bottom-right',
        render: () => (<Toast message={message} icon={<CiWarning />} onClose={() => toast.close(ref)} />),
      });
    }).finally(() => {
      setLoadingFile(false);
      setWaitForLoad(false);
      clearTimeout(time)
    });
  };

  if (!isDropdown) {
    return (
      <Button variant="ghost" marginLeft="auto" marginRight="xl" color="neutral.600" overflow="hidden"
              onClick={() => handleDownload(availableFormats[0])} isDisabled={loadingFile} data-testid="export-button">
        {loadingFile
          ? <>
            <Spinner data-testid="loading-spinner"/>
            {waitForLoad &&
              <SlideFade in={waitForLoad} offsetX="20em" offsetY="0">
                <Text size="s" marginLeft="xl" color="neutral.400">Generando archivo...</Text>
              </SlideFade>
            }
          </>
          : <><FiDownload fontSize="1.2em" /> <Text marginLeft="l">{label}</Text></>
        }
      </Button>
    );
  }

  return <>
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        marginLeft="auto"
        marginRight="6"
        color="gray.600"
        isDisabled={loadingFile}
        isLoading={loadingFile}
        iconSpacing="8px"
        leftIcon={loadingFile ? <Spinner fontSize="1.3em" data-testid="loading-spinner" /> : undefined}
      >
        {loadingFile ? (
          <>
            <Spinner data-testid="loading-spinner"/>
            {waitForLoad && (
              <SlideFade in={waitForLoad} offsetX="20px">
                <Text  color="gray">
                  Generando archivo...
                </Text>
              </SlideFade>
            )}
          </>
        ) : (
          <Flex style={{ display: 'flex', alignItems: 'center', color: 'gray.400' }}>
            <FiDownload fontSize="1.3em" stroke={"gray"} />
            <Text marginLeft="l" color="gray">{label}</Text>
          </Flex>
        )}
      </MenuButton>
      <MenuList zIndex={10}>
        {EXPORT_TYPES
          .filter(type => availableFormats.includes(type.value))
          .map((type, index) => (
            <MenuItem
              key={index}
              icon={type.icon}
              onClick={() => handleDownload(type.value)}
              isDisabled={loadingFile}
            >
              <Text marginLeft="l" color="gray">{type.label}</Text>
            </MenuItem>
        ))}
      </MenuList>
    </Menu>
  </>
}

export default ExportButton
